/* ---------------------------------------------------------------------------
   One defect photograph, enlarged.

   A photograph of a crack at 130px tells you a defect was photographed. Read at
   full size it tells you how wide the crack is — which is the whole reason an
   inspector took it — so the tiles on the record open this rather than jumping
   to the modeller.

   No load of its own. The image is the same URL the tile used, so the browser
   already holds the bytes and the enlarged view appears immediately; see
   api.defectPhotoUrl and functions/api/modeler/defect-photo.ts.

   Rendered into src/main.ts's dialog layer next to the other overlays, and
   dismissed the same three ways they are: the scrim, the close button, Esc.
   --------------------------------------------------------------------------- */

import { h } from '../core/dom.ts';
import { actions, type State } from '../core/store.ts';
import * as api from '../core/api.ts';
import { button, scrim, tag } from '../components/widgets.ts';
import { photoStage } from '../components/photoZoom.ts';
import { progressPhotoDialog } from './progressPhotos.ts';
import { findBridge, inspectionTypeLabel, prettyDate, severityRank } from '../data/derive.ts';
import { defectCodeLabel, defectPhotoCount } from '../data/defects.ts';
import { elementLabel } from '../data/elements.ts';
import type { BridgeView, InspectionPhoto, RegisterDefect } from '../data/types.ts';

/**
 * Where on the structure, in words.
 *
 * The model component when there is one, the coarse DoR element when there is
 * only that, and a plain admission when there is neither — which is a real state:
 * a defect somebody photographed is worth keeping even if they could not say
 * exactly where they were standing.
 */
function where(d: RegisterDefect): string {
  if (d.component) return d.component;
  if (d.element) return elementLabel(d.element);
  return 'Location not recorded';
}

/**
 * The enlarged photograph, or null when none is open.
 *
 * Two sources land here, because to the person looking at it there is one
 * "photograph, bigger" and it should behave identically whichever tile opened
 * it. They are looked up in different places though — a defect photograph
 * against the structure's defects, an inspection photograph in its visit history
 * — so they are separate fields in state and separate branches here.
 *
 * `photoDefectId` is the modeller's handle (`modelDefectId`), not the row's own
 * id, because that is what the photo endpoint is addressed by.
 */
export function photoDialog(s: Readonly<State>): HTMLElement | null {
  if (s.inspectionPhotoId !== null) return inspectionPhotoDialog(s);
  if (s.progressPhotoId !== null) return progressPhotoDialog(s);
  if (s.photoDefectId === null) return null;

  const defect = s.registerDefects.find(
    (d) => d.bridgeId === s.selectedId && d.modelDefectId === s.photoDefectId,
  );
  // The defect can have gone while its photo was open — the list is re-read
  // whenever the modeller closes, and a defect can be withdrawn in it. Nothing
  // to enlarge, so close rather than showing an empty frame over the record.
  if (!defect) return null;

  const b = findBridge(s.selectedId);
  /* No record to hang this on, which happens only where the register is
     empty. Closed rather than drawn over nothing — the same answer this
     dialog already gives when the thing it was opened for has gone. */
  if (b === null) return null;

  /* Which of its photographs. Clamped, because the defect can have been re-read
     with fewer than the number that was open. */
  const count = defectPhotoCount(defect);
  const n = Math.min(Math.max(1, s.photoDefectN), Math.max(1, count));
  const several = count > 1;

  return h(
    'div',
    { class: 'dlg dlg--photo', ...scrim(() => actions.closePhoto()) },
    h(
      'div',
      {
        class: 'photolb',
        role: 'dialog',
        'aria-modal': 'true',
        'aria-label': `${where(defect)} — ${defectCodeLabel(defect.code)}`,
      },

      h(
        'div',
        { class: 'photolb__head' },
        h(
          'div',
          { style: 'min-width:0' },
          h('div', {
            class: 'dlg__kicker',
            text: several ? `${b.id} · issue photograph ${n} of ${count}` : `${b.id} · issue photograph`,
          }),
          h('h2', {
            class: 'photolb__title',
            text: `${where(defect)} — ${defectCodeLabel(defect.code)}`,
          }),
        ),
        h('span', { class: 'spacer' }),
        /* Between the defect's own photographs. The arrow keys do the same —
           see the keydown handler in src/main.ts. */
        several
          ? h(
              'span',
              { class: 'photolb__step' },
              button('‹', {
                sm: true,
                title: 'Previous photograph (←)',
                onClick: () => actions.stepPhoto(-1, count),
              }),
              button('›', {
                sm: true,
                title: 'Next photograph (→)',
                onClick: () => actions.stepPhoto(1, count),
              }),
            )
          : null,
        tag(defect.severity, severityRank(defect.severity)),
        h(
          'button',
          {
            type: 'button',
            class: 'dlg__close',
            title: 'Close (Esc)',
            'aria-label': 'Close',
            onclick: () => actions.closePhoto(),
          },
          '×',
        ),
      ),

      /* The frame fills the window and the photograph zooms inside it — the
         wheel, the toolbar, a double-click. A field photograph off a phone holds
         far more than any window shows at once, and how wide the crack is lives
         in the pixels a fitted view throws away. See photoZoom.ts. */
      photoStage(
        api.defectPhotoSrc(defect, n),
        `${where(defect)} — ${defectCodeLabel(defect.code)}. ${defect.note}`,
      ),

      h(
        'div',
        { class: 'photolb__foot' },
        h(
          'div',
          { class: 'photolb__meta' },
          defect.note
            ? h('div', { class: 'photolb__notes', text: defect.note })
            : h('div', { class: 'photolb__notes photolb__notes--none', text: 'No note on this issue.' }),
          h('div', {
            class: 'photolb__when',
            text: [
              defect.code,
              defect.status,
              defect.loggedAt ? `logged ${prettyDate(defect.loggedAt)}` : null,
            ]
              .filter(Boolean)
              .join(' · '),
          }),
        ),
      ),
    ),
  );
}

/**
 * A photograph taken at an inspection, enlarged.
 *
 * Same frame as the defect view above; what differs is the footer, which carries
 * what an inspection photograph can say for itself — which visit it belongs to,
 * and who uploaded it. That last one is the verified Access identity recorded
 * server-side, so a photograph on the record is attributable to a person rather
 * than to whoever had the tab open.
 */
function inspectionPhotoDialog(s: Readonly<State>): HTMLElement | null {
  const b = findBridge(s.selectedId);
  /* No record to hang this on, which happens only where the register is
     empty. Closed rather than drawn over nothing — the same answer this
     dialog already gives when the thing it was opened for has gone. */
  if (b === null) return null;

  let found: { insp: BridgeView['inspections'][number]; photo: InspectionPhoto } | null = null;
  for (const insp of b.inspections) {
    for (const photo of insp.photos ?? []) {
      if (photo.id === s.inspectionPhotoId) found = { insp, photo };
    }
  }
  /* The structure can have changed under an open photograph — the register is
     re-read whenever a record is saved. Nothing to enlarge, so close rather than
     showing an empty frame. */
  if (!found) return null;

  const { insp, photo } = found;

  return h(
    'div',
    { class: 'dlg dlg--photo', ...scrim(() => actions.closeInspectionPhoto()) },
    h(
      'div',
      {
        class: 'photolb',
        role: 'dialog',
        'aria-modal': 'true',
        'aria-label': `Photograph from the inspection of ${prettyDate(insp.date)}`,
      },

      h(
        'div',
        { class: 'photolb__head' },
        h(
          'div',
          { style: 'min-width:0' },
          h('div', { class: 'dlg__kicker', text: `${b.id} · inspection photograph` }),
          h('h2', {
            class: 'photolb__title',
            text: `${inspectionTypeLabel(insp.type)} inspection — ${prettyDate(insp.date)}`,
          }),
        ),
        h('span', { class: 'spacer' }),
        h(
          'button',
          {
            type: 'button',
            class: 'dlg__close',
            title: 'Close (Esc)',
            'aria-label': 'Close',
            onclick: () => actions.closeInspectionPhoto(),
          },
          '×',
        ),
      ),

      photoStage(
        api.inspectionPhotoUrl(b.id, photo.id),
        `Photograph from the inspection of ${prettyDate(insp.date)}. ${insp.note}`,
      ),

      h(
        'div',
        { class: 'photolb__foot' },
        h(
          'div',
          { class: 'photolb__meta' },
          insp.note
            ? h('div', { class: 'photolb__notes', text: insp.note })
            : h('div', { class: 'photolb__notes photolb__notes--none', text: 'No note on this inspection.' }),
          h('div', {
            class: 'photolb__when',
            /* "Not attributable" rather than "anonymous" where Access was not
               enforced when the photograph was uploaded — the same distinction
               modeler_state.author_email draws, and the reason the field is null
               rather than a placeholder name. */
            text: [
              photo.author ?? 'uploaded before Access was enforced — not attributable',
              `${Math.round(photo.bytes / 1024)} kB`,
            ].join(' · '),
          }),
        ),
      ),
    ),
  );
}
